import { dispatch } from 'pluginServices/store';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import { pushToast } from 'containers/ToastManager/actions';
import ScenarioResolver from 'resolvers/RTMResolvers/ScenarioResolver';
import componentService from 'services/ComponentService';
import interactiveService from 'services/InteractiveService';
import activityService from 'services/ActivityService';

import CoursewareTargetProvider from 'pluginServices/services/CoursewareTargetService';
import * as coursewareTypes from 'pluginServices/constants/coursewareTypes';

import messages from '../messages';
import PreviewScope from '../PreviewScope';
import gtmService from '../../utils/gtm/gtmService';
import { httpErrorCodes, GTM_EVENT_TYPES } from '../../utils/gtm/constants';

/**
 * Takes care of the Preview scope settings and scenario evaluations
 */
class PreviewService {
  constructor() {
    this._scope = new PreviewScope();
    this.isRegistered = false;
    this.scenarioResolver = null;
    this.targetProvider = CoursewareTargetProvider.build();
  }

  setPreviewScope(path, scope) {
    this._scope.setIn(path, scope);
    return this._scope;
  }

  getInPreviewScope(path) {
    return this._scope.getIn(path);
  }

  resetPreviewScope() {
    this._scope = new PreviewScope();
  }

  async updatePreviewScopeAndNotify({ sourceId, context: path, value }) {
    const { type } = await this.targetProvider.createTarget(sourceId);
    this.setPreviewScope([sourceId, ...path], value);
    const sourceScope = this._scope.getIn([sourceId]);
    ({
      [coursewareTypes.ACTIVITY]: activityService.setInPreviewScope,
      [coursewareTypes.INTERACTIVE]: interactiveService.setInPreviewScope,
      [coursewareTypes.COMPONENT]: componentService.setInPreviewScope,
    })[type](sourceId, sourceScope, path);
  }

  _scenarioResponseHandler(scenarioEvaluationResults) {
    if (scenarioEvaluationResults && scenarioEvaluationResults.length > 0) {
      const evalResult = scenarioEvaluationResults.find((result) => result.evaluationResult);
      if (evalResult && evalResult.actions) {
        try {
          const parsedActions = JSON.parse(evalResult.actions);
          const changeScopeActions = parsedActions.filter((action) => action.action === 'CHANGE_SCOPE');
          changeScopeActions.forEach(async ({ context }) => {
            await this.updatePreviewScopeAndNotify(context);
          });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Invalid actions object', error);
        }
      }
    }
  }

  register(connectionProvider) {
    this.isRegistered = true;
    this.scenarioResolver = new ScenarioResolver(connectionProvider);
  }

  generateTriggeredActions(evaluationResults) {
    return evaluationResults.filter((evalRes) => evalRes.evaluationResult).map((evalutationResult) => {
      const parsedActions = JSON.parse(evalutationResult.actions);
      return parsedActions;
    }).flat();
  }

  async evaluateScenario(interactiveId) {
    const scopeData = JSON.stringify(this._scope.get());
    const evaluateResponse = {};

    try {
      const { results: evaluationResults = [] } = await this.scenarioResolver.authorScenarioEvaluate(
        interactiveId,
        scopeData
      );

      const triggeredActions = this.generateTriggeredActions(evaluationResults);
      evaluateResponse.triggeredActions = triggeredActions;
      evaluateResponse.scenarioEvaluationResults = evaluationResults;
      this._scenarioResponseHandler(evaluationResults);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error evaluating ${interactiveId}: ${error.message}`);
      const errorObj = {
        errorMessage: error.message,
        errorCode: httpErrorCodes.UNPROCESSABLE_ENTITY,
        errorDetail: error.message,
        errorStateCode: httpErrorCodes.UNPROCESSABLE_ENTITY,
      };
      gtmService.gtmTrackEvent({ eventType: GTM_EVENT_TYPES.USER_EXCEPTION, payload: errorObj });
      dispatch(pushToast({
        message: intl.formatMessage(messages.evaluationError),
        actions: [
          {
            text: 'Retry',
            fn: () => {
              this.evaluateScenario(interactiveId);
            },
          },
        ],
        waitForAction: true,
      }));
    }
    return evaluateResponse;
  }
}

export default new PreviewService();
